import type { Course, AppSettings, GradePointMapping } from '@gpa-scales/shared-types';
import { GradeFormat } from '@gpa-scales/shared-types';
import {
  LETTER_GRADE_POINTS_PLUS_MINUS,
  LETTER_GRADE_POINTS_NO_PLUS_MINUS,
  PERCENTAGE_TO_LETTER_THRESHOLDS,
  IB_SCALE_MAPPING,
  ECTS_SCALE_MAPPING,
} from '../data/gradePointMappings';

export class InvalidGradeError extends Error {
  constructor(grade: string) {
    super(`Invalid grade: "${grade}"`);
    this.name = 'InvalidGradeError';
  }
}

export function letterToGradePoints(letter: string, plusMinus: boolean): number {
  const normalized = letter.toUpperCase().trim();
  const mappings: GradePointMapping[] = plusMinus
    ? LETTER_GRADE_POINTS_PLUS_MINUS
    : LETTER_GRADE_POINTS_NO_PLUS_MINUS;

  const found = mappings.find((m) => m.grade === normalized);
  if (found) return found.points;

  // If plus/minus is off but we got a +/- grade, try base letter
  if (!plusMinus && normalized.length > 1) {
    const base = normalized.charAt(0);
    const baseFound = mappings.find((m) => m.grade === base);
    if (baseFound) return baseFound.points;
  }

  throw new InvalidGradeError(letter);
}

export function percentageToLetter(pct: number): string {
  const clamped = Math.max(0, Math.min(100, pct));
  for (const threshold of PERCENTAGE_TO_LETTER_THRESHOLDS) {
    if (clamped >= threshold.min) return threshold.letter;
  }
  return 'F';
}

export function resolveGradePoints(course: Course, settings: AppSettings): number | null {
  // Special grades excluded from GPA (anything other than None/empty)
  if (course.specialGrade) {
    return null;
  }

  const grade = course.grade.trim();
  if (!grade) return null;

  switch (course.gradeFormat) {
    case GradeFormat.Letter: {
      return letterToGradePoints(grade, settings.plusMinusEnabled);
    }
    case GradeFormat.Percentage: {
      const pct = parseFloat(grade);
      if (isNaN(pct)) throw new InvalidGradeError(grade);
      const letter = percentageToLetter(pct);
      return letterToGradePoints(letter, settings.plusMinusEnabled);
    }
    case GradeFormat.Numeric: {
      const value = parseFloat(grade);
      if (isNaN(value)) throw new InvalidGradeError(grade);
      return Math.max(0, Math.min(4.0, value));
    }
    case GradeFormat.IB_1_7: {
      const ibGrade = parseInt(grade);
      if (isNaN(ibGrade) || ibGrade < 1 || ibGrade > 7) throw new InvalidGradeError(grade);
      const found = IB_SCALE_MAPPING.find((m) => m.grade === grade.trim());
      return found ? found.points : 0;
    }
    case GradeFormat.ECTS: {
      const normalized = grade.toUpperCase().trim();
      const found = ECTS_SCALE_MAPPING.find((m) => m.grade === normalized);
      if (!found) throw new InvalidGradeError(grade);
      return found.points;
    }
    default:
      throw new InvalidGradeError(grade);
  }
}
